import { cn } from '@/lib/utils';
import hljs from 'highlight.js';
import { useEffect, useRef } from 'react';

import { Card, CardContent, CardHeader } from '@/components/ui/card';

import 'highlight.js/styles/monokai-sublime.css';

import CopyButton from './CopyButton';

interface CodeSnippetProps {
  language: string;
  children: string;
}

const HighlightedCode = ({ language, children }: CodeSnippetProps) => {
  const codeRef = useRef<HTMLElement>(null);

  if (!hljs.getLanguage(language)) {
    language = 'txt';
  }

  useEffect(() => {
    if (codeRef.current) {
      const highlighted =
        codeRef.current.getAttribute('data-highlighted') === 'yes';
      if (!highlighted) {
        hljs.highlightElement(codeRef.current);
      }
    }
  }, []);

  return (
    <pre className="m-0 overflow-x-auto bg-[#0f172a]">
      <code
        ref={codeRef}
        className={`language-${language} block bg-transparent p-4 font-mono text-[13px] leading-6`}
      >
        {children}
      </code>
    </pre>
  );
};

interface CodeProps {
  children: React.ReactNode;
  node?: {
    children?: Array<{
      properties?: {
        className?: string[];
      };
      children?: Array<{
        value?: string;
      }>;
    }>;
  };
}

export default function CodeSnippet({ ...props }: CodeProps) {
  const codeChildren = props.node?.children?.[0];
  const className = codeChildren?.properties?.className?.[0];
  const match = /language-(\w+)/.exec(className || '');
  const code = codeChildren?.children?.[0]?.value;

  const showSyntaxHighlighter = match && code;

  const highlightedCode = showSyntaxHighlighter ? (
    <HighlightedCode language={match[1]}>{code}</HighlightedCode>
  ) : null;

  const nonHighlightedCode = showSyntaxHighlighter ? null : (
    <div
      className={cn(
        'overflow-x-auto bg-[#0f172a] text-slate-100',
        code && 'p-4'
      )}
    >
      <code className="whitespace-pre-wrap border-0 bg-transparent p-0 text-[13px] leading-6 text-inherit">
        {code}
      </code>
    </div>
  );

  return (
    <Card className="relative my-3 overflow-hidden rounded-xl border-border/80 bg-[#0f172a] shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between border-b border-white/10 bg-[#111c31] px-3 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
          {match?.[1] || 'Raw code'}
        </span>
        <CopyButton
          content={code}
          className="size-7 text-slate-400 hover:bg-white/10 hover:text-white"
        />
      </CardHeader>
      <CardContent className="p-0">
        {highlightedCode}
        {nonHighlightedCode}
      </CardContent>
    </Card>
  );
}
